"use client"

import { useState, useEffect, useRef, memo, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Box, CircularProgress, Chip, TextField, InputAdornment } from "@mui/material"
import { Add, People, Search } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import DataTable from "../../components/common/DataTable"
import EmptyState from "../../components/common/EmptyState"
import InfiniteScroll from 'react-infinite-scroll-component';
import { getCustomersPaginated } from '../../services/customer.service';
import { getCustomers, searchCustomers } from "../../services/customer.service"

// Isolated data table component - only re-renders when data changes
const CustomerDataTable = memo(({ 
  customers, 
  loading, 
  isFetching, 
  showLoadMore, 
  hasMore, 
  onFetchNextPage, 
  onLoadMore, 
  onRowClick,
  columns 
}) => {
  const SCROLLABLE_TABLE_ID = 'customers-table-scrollable';

  if (customers.length === 0 && !loading) {
    return (
      <EmptyState
        title="No Customers Found"
        description="Get started by adding your first customer."
        actionText="Add Customer"
        actionIcon={<Add />}
        onActionClick={() => window.location.href = '/customers/create'}
        icon={<People sx={{ fontSize: 60 }} />}
      />
    );
  }

  return (
    <Box id={SCROLLABLE_TABLE_ID} sx={{ height: 600, overflow: 'auto' }}>
      <InfiniteScroll
        dataLength={customers.length}
        next={onFetchNextPage}
        hasMore={!showLoadMore}
        loader={isFetching ? (<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>) : null}
        endMessage={
          showLoadMore && (
            <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
              <b>No more customers to load.</b>
              <Box sx={{ mt: 2 }}>
                <button onClick={onLoadMore} style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>
                  Load More
                </button>
              </Box>
            </Box>
          )
        }
        scrollableTarget={SCROLLABLE_TABLE_ID}
        scrollThreshold={0.98}
      >
        <DataTable 
          columns={columns} 
          data={customers} 
          title="Customers" 
          onRowClick={onRowClick} 
          rowsPerPageOptions={[customers.length]} 
          defaultRowsPerPage={customers.length} 
          hidePagination={true} 
        />
      </InfiniteScroll>
    </Box>
  );
});

const Customers = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [searching, setSearching] = useState(false)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20; // You can adjust this as needed
  const [isFetching, setIsFetching] = useState(false);
  const [noMoreData, setNoMoreData] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(false);
  
  const searchTimeoutRef = useRef(null);

  const organizationId = selectedOrganization?._id || user?.organizationId

  useEffect(() => {
    if (organizationId) {
      setCustomers([]);
      setPage(1);
      setHasMore(true);
      fetchCustomersPaginated(1, true);
    }
  }, [organizationId]);

  const fetchCustomersPaginated = async (pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true);
    setLoading(true);
    try {
      const params = { organizationId, page: pageToFetch, limit};
      if(query) {
        params.search = query;
      }
      const response = await getCustomersPaginated(params);
      let newCustomers = response.data.map((customer) => ({ id: customer._id, ...customer }));
      setTotalCount(response.count || 0);
      if (reset) {
        setCustomers(newCustomers);
        setNoMoreData(false);
        setShowLoadMore(false);
      } else {
        setCustomers((prev) => {
          const existingIds = prev.map((customer) => customer.id);
          newCustomers = newCustomers.filter((customer) => !existingIds.includes(customer.id));
          return [...prev, ...newCustomers];
        });
      }
      if (newCustomers.length < limit) {
        setNoMoreData(true);
        setShowLoadMore(true);
      } else {
        setNoMoreData(false);
        setShowLoadMore(false);
      }
      setHasMore(!showLoadMore);
    } catch (error) {
      showNotification('Failed to fetch customers', 'error');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchNextPage = useCallback(() => {
    if (isFetching || showLoadMore) return;
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      fetchCustomersPaginated(nextPage, false, searchQuery);
      return nextPage;
    });
  }, [isFetching, showLoadMore, totalCount, limit, searchQuery]);

  const handleLoadMore = useCallback(() => {
    setShowLoadMore(false);
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      fetchCustomersPaginated(nextPage, false, searchQuery);
      return nextPage;
    });
  }, [totalCount, limit, searchQuery]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce the API call
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setCustomers([]);
      setNoMoreData(false);
      setShowLoadMore(false);
      fetchCustomersPaginated(1, true, query);
    }, 500);
  };

  const handleCreateCustomer = useCallback(() => {
    navigate("/customers/create")
  }, [navigate])

  const handleRowClick = useCallback((row) => {
    navigate(`/customers/${row.id}`)
  }, [navigate])

  const columns = useMemo(() => [
    { id: "name", label: "Name", minWidth: 170 },
    { id: "email", label: "Email", minWidth: 170 },
    { id: "externalId", label: "External ID", minWidth: 130 },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value === "active" ? "success" : value === "trial" ? "info" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "contactPerson",
      label: "Contact",
      minWidth: 170,
      format: (value) => value?.name || "-",
    },
    {
      id: "createdAt",
      label: "Created",
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
  ], [])

  return (
    <Box>
      <PageHeader
        title="Customers"
        subtitle="Manage your customers"
        breadcrumbs={[{ text: 'Dashboard', link: '/dashboard' }, { text: 'Customers' }]}
        action
        actionText="Add Customer"
        actionIcon={<Add />}
        onActionClick={handleCreateCustomer}
      />
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search customers..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
      {loading && customers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <CustomerDataTable
          customers={customers}
          loading={loading}
          isFetching={isFetching}
          showLoadMore={showLoadMore}
          hasMore={hasMore}
          onFetchNextPage={fetchNextPage}
          onLoadMore={handleLoadMore}
          onRowClick={handleRowClick}
          columns={columns}
        />
      )}
    </Box>
  );
};

export default Customers

